import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { SERVICES } from "@/data/site";
import {
  Seo,
  serviceJsonLd,
  faqJsonLd,
  breadcrumbJsonLd,
} from "@/components/site/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  const path = `/leistungen/${slug}`;
  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div className="lx-noise relative overflow-hidden">
      {service ? (
        <Seo
          path={path}
          title={service.metaTitle || service.title}
          description={service.metaDescription || service.desc}
          jsonLd={[
            serviceJsonLd({
              name: service.title,
              description: service.metaDescription || service.desc,
              path,
            }),
            service.faqs && service.faqs.length ? faqJsonLd(service.faqs) : null,
            breadcrumbJsonLd([
              { name: "Start", path: "/" },
              { name: "Leistungen", path: "/#leistungen" },
              { name: service.title, path },
            ]),
          ].filter(Boolean)}
        />
      ) : (
        <Seo path={path} title="Leistung nicht gefunden" noindex />
      )}

      <div className="lx-hero-ambient absolute inset-0 -z-10 opacity-70" />
      <div className="mx-auto max-w-5xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32 lg:px-8 lg:pb-28 lg:pt-40">
        <Link
          to="/#leistungen"
          data-testid="service-back-link"
          className="lx-navlink inline-flex items-center gap-2 font-mono-lx text-xs uppercase tracking-[0.18em]"
        >
          <ArrowLeft size={14} /> Alle Leistungen
        </Link>

        {!service ? (
          <div className="mt-10">
            <p className="text-[color:var(--lx-text-2)]">
              Diese Leistung konnte nicht gefunden werden.
            </p>
            <Link
              to="/#leistungen"
              className="mt-4 inline-flex items-center gap-2 font-display text-[color:var(--lx-green)]"
            >
              Zurück zu den Leistungen <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <motion.div variants={stagger} initial="hidden" animate="show">
            {/* Header */}
            <motion.div variants={fadeUp} className="mt-8 flex items-center gap-4">
              <span
                className="grid h-14 w-14 shrink-0 place-items-center rounded-[16px] border"
                style={{
                  borderColor: "var(--lx-border-strong)",
                  background: "var(--lx-surface-2)",
                  color: "var(--lx-green)",
                }}
              >
                <service.icon size={26} />
              </span>
              <span className="lx-kicker">Leistung</span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              data-testid="service-title"
              className="mt-6 font-display text-4xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-5xl"
            >
              {service.title}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-4 max-w-2xl text-lg leading-relaxed text-[color:var(--lx-green-soft)]"
            >
              {service.intro}
            </motion.p>

            {(service.long || []).map((p, i) => (
              <motion.p
                key={i}
                variants={fadeUp}
                className="mt-5 max-w-2xl text-base leading-relaxed text-[color:var(--lx-text-2)]"
              >
                {p}
              </motion.p>
            ))}

            <motion.div variants={fadeUp} className="mt-7">
              <Link
                to="/#kontakt"
                data-testid="service-cta"
                className="group inline-flex items-center gap-2 rounded-[12px] px-5 py-3 font-display font-medium"
                style={{ background: "var(--lx-green)", color: "var(--lx-canvas)" }}
              >
                Projekt anfragen
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </motion.div>

            {/* Included + Benefits */}
            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
              {service.points && (
                <motion.div variants={fadeUp} className="lx-card p-7">
                  <h2 className="font-display text-lg font-semibold text-[color:var(--lx-text)]">
                    Das ist enthalten
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {service.points.map((p) => (
                      <li key={p} className="flex items-start gap-3">
                        <Check size={16} className="mt-0.5 shrink-0" style={{ color: "var(--lx-green)" }} />
                        <span className="text-sm leading-relaxed text-[color:var(--lx-text-2)]">{p}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
              {service.benefits && (
                <motion.div variants={fadeUp} className="lx-card p-7">
                  <h2 className="font-display text-lg font-semibold text-[color:var(--lx-text)]">
                    Deine Vorteile
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {service.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <Sparkles size={16} className="mt-0.5 shrink-0" style={{ color: "var(--lx-green)" }} />
                        <span className="text-sm leading-relaxed text-[color:var(--lx-text-2)]">{b}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>

            {/* Tech */}
            {service.tech && (
              <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-2">
                {service.tech.map((t) => (
                  <span
                    key={t}
                    className="rounded-full border px-3 py-1 font-mono-lx text-[11px]"
                    style={{ borderColor: "var(--lx-border)", color: "var(--lx-muted)" }}
                  >
                    <span style={{ color: "var(--lx-green-dim)" }}>#</span>
                    {t}
                  </span>
                ))}
              </motion.div>
            )}

            {/* FAQ */}
            {service.faqs && service.faqs.length > 0 && (
              <section className="mt-16">
                <div className="lx-kicker mb-3">FAQ</div>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
                  Häufige Fragen
                </h2>
                <Accordion type="single" collapsible className="mt-6 w-full">
                  {service.faqs.map((f, i) => (
                    <AccordionItem
                      key={i}
                      value={`item-${i}`}
                      data-testid="service-faq-item"
                      style={{ borderColor: "var(--lx-border)" }}
                    >
                      <AccordionTrigger className="text-left font-display text-[15px] text-[color:var(--lx-text)]">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed text-[color:var(--lx-text-2)]">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            )}

            {/* Other services */}
            <section className="mt-16">
              <div className="lx-kicker mb-3">Weitere Leistungen</div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {others.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/leistungen/${s.slug}`}
                    data-testid="service-related-link"
                    className="lx-card group flex items-center gap-3 p-4 transition-colors hover:border-[color:var(--lx-border-strong)]"
                  >
                    <span
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border"
                      style={{
                        borderColor: "var(--lx-border-strong)",
                        background: "var(--lx-surface-2)",
                        color: "var(--lx-green)",
                      }}
                    >
                      <s.icon size={18} />
                    </span>
                    <span className="font-display text-sm text-[color:var(--lx-text)] transition-colors group-hover:text-[color:var(--lx-green-soft)]">
                      {s.title}
                    </span>
                    <ArrowRight size={15} className="ml-auto shrink-0 text-[color:var(--lx-muted)] transition-transform group-hover:translate-x-0.5" />
                  </Link>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="mt-16">
              <div
                className="lx-terminal p-8 text-center sm:p-12"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(168,232,92,0.07), rgba(14,20,16,0.9))",
                  borderColor: "rgba(168,232,92,0.28)",
                }}
              >
                <Sparkles size={26} className="mx-auto" style={{ color: "var(--lx-green)" }} />
                <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
                  Bereit für {service.title}?
                </h2>
                <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-[color:var(--lx-text-2)] sm:text-base">
                  Erzähl uns von deiner Idee, transparent, mit 100% Geld-zurück-Garantie und laufenden Updates.
                </p>
                <Link
                  to="/#kontakt"
                  className="group mt-6 inline-flex items-center gap-2 rounded-[12px] px-6 py-3 font-display font-medium"
                  style={{ background: "var(--lx-green)", color: "var(--lx-canvas)" }}
                >
                  Projekt anfragen
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </section>
          </motion.div>
        )}
      </div>
    </div>
  );
}
